import React from 'react';
import { Typography, Box, Stack } from '@mui/material';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import { REVIEW_TYPES } from '../../../constants/reviewTypes';
import { formatTemplateType, getCompetencyColor, getCompetencyLevelName, getGaugeMaxValue } from '../../../utils/feedbackReportUtils';

const CompetencyDashboard = ({ templateTypeStats, overallPerformance, skillLevels, reviewType }) => {
  return (
    <Stack 
      direction={{ xs: 'column', md: 'row' }} 
      spacing={2} 
      flex={{ xs: '1', md: '7' }}
      sx={{ 
        mt: { xs: 3, md: 0 },
        ml: 'auto',
        mr: 'auto'
      }}
    >
      {/* Competency by Template Type */}
      <Box flex={{ xs: '1', md: '7' }}>
        <Box sx={{ 
          p: 2, 
          border: '1px solid #e0e0e0', 
          borderRadius: 1, 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}>
          <Typography variant="h6" gutterBottom align="center">
            Competency by Template Type
          </Typography>
          
          {reviewType === REVIEW_TYPES.SEQUENTIAL && (
            <Typography variant="caption" color="text.secondary" align="center" sx={{ mb: 2, display: 'block' }}>
              Previous reviewers only (final reviewer shown in Overall Performance)
            </Typography>
          )}
          
          {templateTypeStats.length > 0 ? (
            <>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                gap: 2,
                width: '100%',
                overflowX: 'auto'
              }}>
                {templateTypeStats
                  .filter(stats => stats.type !== "overall")
                  .map((stats, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      minWidth: '160px',
                      flex: '1 1 0',
                      mb: 2,
                      mx: 1
                    }}
                  >
                    <Typography variant="subtitle1" align="center" gutterBottom>
                      {formatTemplateType(stats.type)}
                    </Typography>
                    <Gauge
                      value={stats.average}
                      valueMin={0}
                      valueMax={getGaugeMaxValue(skillLevels, reviewType)}
                      sx={{
                        width: 140,
                        height: 140,
                        [`& .${gaugeClasses.valueText}`]: {
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                        },
                        [`& .${gaugeClasses.valueArc}`]: {
                          fill: getCompetencyColor(stats.average, skillLevels, reviewType),
                        },
                      }}
                      text={`${stats.average}`}
                      startAngle={-110}
                      endAngle={110}
                    />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {getCompetencyLevelName(stats.average, skillLevels)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" align="center">
                      Based on {stats.count} responses
                    </Typography>
                  </Box>
                ))}
              </Box>
              
              <CompetencyLegend skillLevels={skillLevels} reviewType={reviewType} />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              No competency data available
            </Typography>
          )}
        </Box>
      </Box>

      {/* Overall Performance */}
      <Box flex={{ xs: '1', md: '5' }}>
        <Box sx={{ 
          p: 2, 
          border: '1px solid #e0e0e0', 
          borderRadius: 1, 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start'
        }}>
          <Typography variant="h6" gutterBottom align="center">
            Overall Performance
          </Typography>
          
          <Typography variant="caption" color="text.secondary" align="center" sx={{ mb: 2, display: 'block' }}>
            {reviewType === REVIEW_TYPES.SEQUENTIAL 
              ? "Based exclusively on final reviewer's assessment" 
              : "Combined across all templates"}
          </Typography>
          
          {overallPerformance.count > 0 ? (
            <>
              <Gauge
                value={overallPerformance.average}
                valueMin={0}
                valueMax={getGaugeMaxValue(skillLevels, reviewType)}
                sx={{
                  width: 140,
                  height: 140,
                  [`& .${gaugeClasses.valueText}`]: {
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                  },
                  [`& .${gaugeClasses.valueArc}`]: {
                    fill: getCompetencyColor(overallPerformance.average, skillLevels, reviewType),
                  },
                }}
                text={`${overallPerformance.average}`}
                startAngle={-110}
                endAngle={110}
              />
              <Typography variant="body2" sx={{ mt: 1 }}>
                {getCompetencyLevelName(overallPerformance.average, skillLevels)}
              </Typography>
              <Typography variant="caption" color="text.secondary" align="center">
                Based on {overallPerformance.count} responses
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              No performance data available
            </Typography>
          )}
        </Box>
      </Box>
    </Stack>
  );
};

const CompetencyLegend = ({ skillLevels, reviewType }) => {
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      mt: 2,
      gap: 2
    }}>
      {skillLevels && skillLevels.length > 0 ? (
        <>
          {(() => {
            const validLevels = 
            reviewType === REVIEW_TYPES.SEQUENTIAL ?
              skillLevels.filter(l => l.level >= 0) :
              skillLevels.filter(l => l.level > 0);
            
            if (validLevels.length === 0) {
              return (
                <>
                  <LegendItem color="#f44336" label="Low" />
                  <LegendItem color="#ff9800" label="Medium" />
                  <LegendItem color="#4caf50" label="High" />
                </>
              );
            }
            
            const lowThreshold = Math.ceil(validLevels.length / 3);
            const mediumThreshold = Math.ceil((2 * validLevels.length) / 3);
            
            return (
              <>
                <LegendItem 
                  color="#f44336" 
                  label={`Low ${lowThreshold === 1 ? '(1)' : `(1-${lowThreshold})`}`} 
                />
                <LegendItem 
                  color="#ff9800" 
                  label={`Medium ${lowThreshold + 1 === mediumThreshold ? `(${lowThreshold + 1})` : `(${lowThreshold + 1}-${mediumThreshold})`}`} 
                />
                <LegendItem 
                  color="#4caf50" 
                  label={`High ${mediumThreshold + 1 === validLevels.length ? `(${validLevels.length})` : `(${mediumThreshold + 1}-${validLevels.length})`}`} 
                />
              </>
            );
          })()}
        </>
      ) : (
        <>
          <LegendItem color="#f44336" label="Low (<3)" />
          <LegendItem color="#ff9800" label="Medium (3-4)" />
          <LegendItem color="#4caf50" label="High (5-7)" />
        </>
      )}
    </Box>
  );
};

const LegendItem = ({ color, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <Box sx={{ 
      width: 12, 
      height: 12, 
      borderRadius: '50%', 
      bgcolor: color, 
      mr: 0.5 
    }} />
    <Typography variant="caption">{label}</Typography>
  </Box>
);

export default CompetencyDashboard; 